import styles from './index.module.css'

import type { CellContext } from '@tanstack/react-table'
import React from 'react'

import { NEW_SEARCH_MDS_ENABLE } from '@disco/constants/FeatureTypes'
import {
  SEARCH_MDS_DISPLAY_NAME,
  SEARCH_MY_DISCO_DISPLAY_NAME,
  SOURCE_SCOPE,
} from '@disco/constants/search'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'

import type { Track } from 'types/track'

import { Text } from 'components/ds'
import { sanitizeColumnId } from 'components/ds/DataTable/table-utils'

import { CatalogInfoPopover } from './catalog-info-popover'

const trimString = (v: unknown): string =>
  typeof v === 'string' ? v.trim() : ''

// Persists for session - failed URLs unlikely to recover
const failedLogoUrls = new Set<string>()

export const __resetFailedLogos = (): void => {
  failedLogoUrls.clear()
}

export const getSourceLabel = (row: Track): string => {
  if (row.scope === SOURCE_SCOPE.MDS) {
    const mdsName = trimString(row.source?.business_name)
    return mdsName || SEARCH_MDS_DISPLAY_NAME
  }
  return SEARCH_MY_DISCO_DISPLAY_NAME
}

type Props = { info: CellContext<Track, unknown> }

const SourceColumnInternal = ({ info }: Props) => {
  const row = info.row.original
  const label = getSourceLabel(row)
  const logoUrl = trimString(row.origin?.business?.logo)
  const isMdsRow = row.scope === SOURCE_SCOPE.MDS
  const mdsEnabled = useFeatureEnabled(NEW_SEARCH_MDS_ENABLE)
  const [logoFailed, setLogoFailed] = React.useState(() =>
    logoUrl ? failedLogoUrls.has(logoUrl) : false,
  )
  const handleError = React.useCallback(() => {
    if (logoUrl) failedLogoUrls.add(logoUrl)
    setLogoFailed(true)
  }, [logoUrl])

  if (!label) return null

  const colId = sanitizeColumnId(String(info.column.id))
  const labelNode = <Text className={styles.label}>{label}</Text>
  return (
    <span
      className={styles.sourceCell}
      // --col-*-size is injected per-render by TanStack Table; not a static class.
      style={{
        maxWidth: `calc(var(--col-${colId}-size) * 1px - 20px)`,
      }}
      data-testid="source-cell"
    >
      {logoUrl && !logoFailed ? (
        <img
          className={styles.logo}
          src={logoUrl}
          alt={`${label} logo`}
          data-testid="source-logo"
          onError={handleError}
        />
      ) : null}
      {isMdsRow && mdsEnabled && row.source != null ? (
        <CatalogInfoPopover source={row.source}>{labelNode}</CatalogInfoPopover>
      ) : (
        labelNode
      )}
    </span>
  )
}

export const SourceColumn = React.memo(SourceColumnInternal)

SourceColumn.displayName = 'SourceColumn'
